/**
 * crossWindowDrag — carry a LIVE pointer drag across same-origin windows over
 * BroadcastChannel (the "press ⌥ mid-drag and the mode switches" gesture).
 *
 * Native HTML5 DnD (useCrossTabDrop's transport) can only start from a fresh
 * mousedown on a `draggable` element — a pointer gesture can never convert to
 * it mid-flight. But it doesn't need to: pointer capture keeps delivering
 * move/up events to the SOURCE window even while the cursor is over another
 * window, with valid SCREEN coordinates. So the source window broadcasts
 * {screen x/y} plus the item payload, and every other builder window maps the
 * screen point into its own viewport and drives the SAME external-add
 * pipeline the native drop uses (blue landing slot, membership gating,
 * zoom-correct landing, single-commit drop). Copy semantics throughout —
 * the source item never moves; dropping back on the source window is
 * duplicate-here through the identical path.
 *
 * Receivers ignore everything while `document.hidden` — background tabs
 * share screen coordinates with the windows above them and must never steal
 * a drop.
 */
import { useEffect, useRef } from "react";
import type { UseWebCrossGridDragReturn } from "@fredericrous/grid/headless";
import {
  buildCrossTabPayload,
  isCrossTabDraggable,
  parseMetaType,
  encodeMetaType,
  type CrossTabMeta,
  type CrossTabPayload,
} from "./crossTabDrag.js";
import { commitCrossTabPayload, crossTabTargetAt } from "./useCrossTabDrop.js";
import { useSiteId } from "./SiteContext.js";
import { cloneItemNode } from "./cloneItemNode.js";
import { textFallbackOf } from "./crossTabDrag.js";
import { useGridStore } from "@fredericrous/grid";

export const XWIN_CHANNEL = "wb:xwindow-drag";

export type XWinMsg =
  | { t: "xw-start"; payload: CrossTabPayload; meta: CrossTabMeta }
  | { t: "xw-move"; sx: number; sy: number }
  | { t: "xw-drop"; sx: number; sy: number }
  | { t: "xw-cancel" };

/** The slice of BroadcastChannel used — injectable for tests. */
export interface ChannelLike {
  postMessage(m: unknown): void;
  close(): void;
  onmessage: ((ev: MessageEvent) => void) | null;
}

/** Map SCREEN coordinates into THIS window's client space. Chrome's side
 *  borders are ~0; the top browser chrome is outer−inner height. Exported
 *  for tests (window metrics injectable). */
export function screenToClient(
  sx: number,
  sy: number,
  win: {
    screenX: number;
    screenY: number;
    outerHeight: number;
    innerHeight: number;
  } = window,
): { x: number; y: number } {
  return {
    x: sx - win.screenX,
    y: sy - win.screenY - (win.outerHeight - win.innerHeight),
  };
}

function insideViewport(p: { x: number; y: number }): boolean {
  return (
    p.x >= 0 &&
    p.y >= 0 &&
    p.x <= window.innerWidth &&
    p.y <= window.innerHeight
  );
}

/** True in the window that OWNS the live carry: its receiver skips the
 *  generic payload ghost — the sender shows the real item clone instead. */
let carrySourceActive = false;

/** The CURRENT canvas zoom of this window, read off xyflow's viewport
 *  transform — the ghost must render at layout-size x zoom so it matches the
 *  size the item actually occupies on screen here (user calibration: the
 *  in-canvas move ghost "looks good", and it renders exactly that way). */
export function canvasZoom(): number {
  const vp = document.querySelector(".react-flow__viewport");
  if (!(vp instanceof HTMLElement)) return 1;
  const m = /scale\(([\d.]+)\)/.exec(vp.style.transform || "");
  const z = m ? parseFloat(m[1] ?? "") : NaN;
  return Number.isFinite(z) && z > 0 ? z : 1;
}

/** Inline `--wb-*` theme vars from the nearest themed ancestor of `el` — the
 *  clone renders OUTSIDE the page node, so without them its typography falls
 *  back to app defaults (serif canvas vs sans ghost, the font-drift bug). */
function themeVarsAbove(el: Element | null): Record<string, string> {
  let cur: HTMLElement | null = el instanceof HTMLElement ? el : null;
  while (cur && cur !== document.body) {
    const st = cur.style;
    const vars: Record<string, string> = {};
    for (let i = 0; i < st.length; i++) {
      const nm = st[i];
      if (nm && nm.startsWith("--wb-")) vars[nm] = st.getPropertyValue(nm);
    }
    if (Object.keys(vars).length) return vars;
    cur = cur.parentElement;
  }
  return {};
}

/**
 * The traveling visual of a carry — a fixed-position card that follows the
 * mapped pointer AT THE SIZE THE ITEM OCCUPIES ON SCREEN in this window
 * (layout px x live canvas zoom, re-read every frame so pinch-zooming
 * mid-carry stays true). The SOURCE window fills it with a DOM clone of the
 * real item; a RECEIVING window synthesizes content from the payload at the
 * item's layout size — same box, approximate typography.
 */
export interface CarryGhost {
  move(x: number, y: number): void;
  hide(): void;
  dispose(): void;
  /** Current on-screen box of the ghost (the clone's zoom-scaled metrics at
   *  the last painted anchor), or null before the first move / after dispose.
   *  The mirror-switch's `startCrossGridDragAt` rect fallback when the real
   *  item element is gone. */
  rect(): { left: number; top: number; width: number; height: number } | null;
}

export function createCarryGhost(content: {
  cloneOf?: string;
  payload?: CrossTabPayload;
}): CarryGhost {
  const wrap = document.createElement("div");
  wrap.dataset["testid"] = "carry-ghost";
  wrap.style.cssText =
    "position:fixed;left:0;top:0;z-index:2100;pointer-events:none;" +
    "display:none;opacity:0.95;background:#fff;border:1px solid #e5e7eb;" +
    "border-radius:6px;overflow:hidden;box-shadow:0 8px 24px rgba(0,0,0,0.28);";
  // Layout-px box of the item (source: measured clone; receiver: payload).
  let lw = 480;
  let lh = 160;
  const inner = document.createElement("div");
  inner.style.cssText =
    "transform-origin:top left;pointer-events:none;background:#fff;";
  const clone = content.cloneOf ? cloneItemNode(content.cloneOf) : null;
  if (clone) {
    lw = clone.w;
    lh = clone.h;
    // Theme context: the clone leaves the page node, so carry its --wb-* vars
    // (typography) onto the ghost or the fonts drift.
    const src = document.querySelector(
      `[data-grid-item="${typeof CSS !== "undefined" && CSS.escape ? CSS.escape(content.cloneOf!) : content.cloneOf}"]`,
    );
    const vars = themeVarsAbove(src);
    for (const [k, v] of Object.entries(vars)) wrap.style.setProperty(k, v);
    wrap.style.fontFamily = "var(--wb-font-body, inherit)";
    inner.appendChild(clone.node);
  } else if (content.payload) {
    const px = content.payload.item.pxSize;
    if (px) {
      lw = px.w;
      lh = px.h;
    }
    const text = textFallbackOf(content.payload.item.contentProps);
    const label =
      text.trim().slice(0, 160) ||
      (content.payload.item.contentType === "image"
        ? "🖼"
        : content.payload.item.contentType);
    inner.style.cssText +=
      "padding:16px 18px;font:500 26px/1.25 system-ui,sans-serif;color:#111;overflow:hidden;";
    inner.textContent = label;
  }
  inner.style.width = `${lw}px`;
  inner.style.height = `${lh}px`;
  wrap.appendChild(inner);
  document.body.appendChild(wrap);
  let disposed = false;
  let lastZoom = 0;
  let lastX = Number.NaN;
  let lastY = Number.NaN;
  return {
    move(x, y) {
      if (disposed) return;
      const z = canvasZoom();
      if (z !== lastZoom) {
        lastZoom = z;
        wrap.style.width = `${Math.round(lw * z)}px`;
        wrap.style.height = `${Math.round(lh * z)}px`;
        inner.style.transform = `scale(${z})`;
      }
      lastX = x;
      lastY = y;
      wrap.style.display = "block";
      // Anchor slightly under the cursor, like a grab near the top edge.
      wrap.style.transform = `translate(${Math.round(x - (lw * z) / 2)}px, ${Math.round(y - 18)}px)`;
    },
    rect() {
      if (disposed || Number.isNaN(lastX)) return null;
      const z = lastZoom || 1;
      const w = lw * z;
      const h = lh * z;
      // Same anchor math as move(): grab centred horizontally, 18px down.
      return { left: lastX - w / 2, top: lastY - 18, width: w, height: h };
    },
    hide() {
      if (!disposed) wrap.style.display = "none";
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      wrap.remove();
    },
  };
}

export interface CrossWindowReceiver {
  handle(msg: XWinMsg): void;
  dispose(): void;
}

/** Receiving side: drives the external-add preview/commit from broadcast
 *  frames. One instance per window; also fed directly by the local sender
 *  (BroadcastChannel never delivers to its own poster). */
export function createCrossWindowReceiver(opts: {
  siteId: () => string | null;
  targetAt?: typeof crossTabTargetAt;
  /** Test seam: defaults to `document.hidden`. */
  hidden?: () => boolean;
}): CrossWindowReceiver {
  const targetAt = opts.targetAt ?? crossTabTargetAt;
  const hidden = opts.hidden ?? (() => document.hidden);
  let session: { payload: CrossTabPayload; meta: CrossTabMeta } | null = null;
  let previewing = false;
  let ghost: CarryGhost | null = null;

  const stopPreview = () => {
    ghost?.dispose();
    ghost = null;
    if (!previewing) return;
    useGridStore.getState().cancelExternalAdd();
    previewing = false;
  };

  return {
    handle(msg) {
      if (msg.t === "xw-start") {
        // The payload arrives over a same-origin channel; the meta must still
        // describe it (one validation shared with the DnD path's type string).
        const meta = parseMetaType([encodeMetaType(msg.payload)]);
        if (!meta || msg.payload.kind !== "app") return;
        session = { payload: msg.payload, meta: msg.meta };
        return;
      }
      if (!session) return;
      if (msg.t === "xw-cancel") {
        stopPreview();
        session = null;
        return;
      }
      const p = screenToClient(msg.sx, msg.sy);
      if (msg.t === "xw-move") {
        if (hidden() || !insideViewport(p)) {
          stopPreview();
          return;
        }
        // Traveling visual: the carry SOURCE shows the real item clone
        // (sender-side); every other window rebuilds a card from the payload.
        if (!carrySourceActive) {
          ghost ??= createCarryGhost({ payload: session.payload });
          ghost.move(p.x, p.y);
        }
        const store = useGridStore.getState();
        if (!previewing) {
          const id = store.startExternalAdd({
            contentType: session.meta.ct,
            w: session.meta.w,
            h: session.meta.h,
          });
          if (id === null) return; // another interaction is live here
          previewing = true;
        }
        store.updateExternalAdd(p.x, p.y, targetAt(p.x, p.y, session.meta));
        return;
      }
      // xw-drop
      const { payload, meta } = session;
      session = null;
      const destSiteId = opts.siteId();
      if (hidden() || !insideViewport(p) || !destSiteId) {
        stopPreview();
        return;
      }
      stopPreview();
      commitCrossTabPayload(payload, meta, p.x, p.y, destSiteId, targetAt);
    },
    dispose() {
      stopPreview();
      session = null;
    },
  };
}

export interface CrossWindowCarry {
  /** Abort the carry (⌥ released, Escape): previews everywhere clear and the
   *  gesture is handed back to the caller (who may restart the move drag). */
  cancel(): void;
  /** Last pointer position seen (client space of the SOURCE window) — the
   *  restart point for the move drag on ⌥ release. */
  readonly lastClient: { x: number; y: number; pointerId: number };
  /** On-screen box of the source ghost (the item clone's metrics) — the
   *  restart rect when the real item element is no longer in the DOM. Read
   *  it BEFORE cancel() (teardown disposes the ghost). */
  lastRect(): {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null;
}

/** Sender side: begins broadcasting the live gesture. The caller has already
 *  ended the grid's pointer drag; this takes over the still-held pointer. */
export function startCrossWindowCarry(opts: {
  itemId: string;
  gridId: string;
  siteId: string;
  channel: ChannelLike;
  /** The local window's receiver — fed directly (a channel never echoes). */
  local: CrossWindowReceiver;
  startClient: { x: number; y: number; pointerId: number };
  /** The gesture ended on its own (drop or cancel) — the mode manager clears
   *  its state. NOT fired by {@link CrossWindowCarry.cancel}. */
  onEnd: (reason: "drop" | "cancel") => void;
}): CrossWindowCarry | null {
  const item = useGridStore.getState().grids[opts.gridId]?.items[opts.itemId];
  // Layout-px size of the on-canvas content (zoom-independent) for the
  // receiver's px-true landing refit.
  const contentEl = document.querySelector(
    `[data-grid-item="${typeof CSS !== "undefined" && CSS.escape ? CSS.escape(opts.itemId) : opts.itemId}"]`,
  )?.firstElementChild;
  const pxSize =
    contentEl instanceof HTMLElement && contentEl.offsetWidth > 0
      ? { w: contentEl.offsetWidth, h: contentEl.offsetHeight }
      : undefined;
  const payload = item
    ? buildCrossTabPayload(item, opts.siteId, window.location.origin, pxSize)
    : null;
  const meta = payload ? parseMetaType([encodeMetaType(payload)]) : null;
  if (!payload || !meta) return null;

  const last = {
    x: opts.startClient.x,
    y: opts.startClient.y,
    pointerId: opts.startClient.pointerId,
  };
  // The source window's traveling visual: the REAL item, cloned — the ghost
  // the grid drag was showing dies with the Escape-cancel, and without a
  // replacement the tile just vanished the instant ⌥ went down (user report
  // 2026-07-19). Follows the pointer while it is over THIS window; other
  // windows render their own payload ghost (receiver side).
  carrySourceActive = true;
  const ghost = createCarryGhost({ cloneOf: opts.itemId });
  const paintGhost = (x: number, y: number) => {
    if (x >= 0 && y >= 0 && x <= window.innerWidth && y <= window.innerHeight)
      ghost.move(x, y);
    else ghost.hide();
  };
  paintGhost(last.x, last.y);
  const post = (m: XWinMsg) => {
    opts.channel.postMessage(m);
    opts.local.handle(m);
  };
  // Client → screen for the SOURCE window (inverse of screenToClient).
  const toScreen = (x: number, y: number) => ({
    sx: x + window.screenX,
    sy: y + window.screenY + (window.outerHeight - window.innerHeight),
  });

  let done = false;
  const teardown = () => {
    if (done) return;
    done = true;
    carrySourceActive = false;
    ghost.dispose();
    window.removeEventListener("pointermove", onMove, true);
    window.removeEventListener("pointerup", onUp, true);
    window.removeEventListener("keydown", onKey, true);
  };
  const onMove = (e: PointerEvent) => {
    last.x = e.clientX;
    last.y = e.clientY;
    last.pointerId = e.pointerId;
    paintGhost(e.clientX, e.clientY);
    const { sx, sy } = toScreen(e.clientX, e.clientY);
    post({ t: "xw-move", sx, sy });
  };
  const onUp = (e: PointerEvent) => {
    const { sx, sy } = toScreen(e.clientX, e.clientY);
    teardown();
    post({ t: "xw-drop", sx, sy });
    opts.onEnd("drop");
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    teardown();
    post({ t: "xw-cancel" });
    opts.onEnd("cancel");
  };
  window.addEventListener("pointermove", onMove, true);
  window.addEventListener("pointerup", onUp, true);
  window.addEventListener("keydown", onKey, true);

  post({ t: "xw-start", payload, meta });
  const { sx, sy } = toScreen(last.x, last.y);
  post({ t: "xw-move", sx, sy });

  return {
    cancel() {
      teardown();
      post({ t: "xw-cancel" });
    },
    get lastClient() {
      return last;
    },
    lastRect: () => ghost.rect(),
  };
}

/**
 * useCrossWindowDrag — mounts the receiver for this window AND the ⌥
 * mid-drag mode switch:
 *
 *   • ⌥ pressed while a pointer MOVE drag is live → the grid drag cancels
 *     (the adapter's own `cancel()`, grid 0.9.4 — identical to Esc) and the
 *     SAME held gesture continues as a cross-window COPY carry. The tile
 *     springs home; landing previews follow the cursor in whichever builder
 *     window it is over — including this one (duplicate-here).
 *   • ⌥ released mid-carry → the carry cancels and the MOVE drag restarts
 *     at the current pointer position via the adapter's point-based
 *     `startCrossGridDragAt` (the mirror switch — no synthetic React event).
 *   • Release the button → drop commits in the window under the cursor.
 *
 * `adapter` is the canvas's cross-grid adapter instance (the same one grid
 * items start their drags through) — needed for the switch-over/switch-back.
 */
export function useCrossWindowDrag(
  readOnly: boolean,
  adapter: Pick<UseWebCrossGridDragReturn, "startCrossGridDragAt" | "cancel">,
): void {
  const siteId = useSiteId();
  const siteIdRef = useRef(siteId);
  siteIdRef.current = siteId;
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  useEffect(() => {
    if (readOnly || typeof window === "undefined") return;
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(XWIN_CHANNEL);
    const receiver = createCrossWindowReceiver({
      siteId: () => siteIdRef.current,
    });
    channel.onmessage = (ev) => receiver.handle(ev.data as XWinMsg);

    let carry: (CrossWindowCarry & { itemId: string; gridId: string }) | null =
      null;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Alt" || e.repeat || carry) return;
      const cg = useGridStore.getState().interaction.crossGrid;
      if (!cg || cg.kind !== "app") return;
      // Pinned shared-region defaults never leave their band — no carry.
      if (!isCrossTabDraggable({ id: cg.itemId, contentType: cg.kind })) return;
      const sid = siteIdRef.current;
      if (!sid) return;
      const start = {
        x: cg.pointer.x,
        y: cg.pointer.y,
        pointerId: 1,
      };
      const itemId = cg.itemId;
      const gridId = cg.fromGridId;
      // End the adapter's pointer session through its programmatic cancel
      // (grid 0.9.4 — same path as Esc, no synthetic keyboard event): the
      // tile springs home (copy semantics: the original never travels).
      adapterRef.current.cancel();
      const c = startCrossWindowCarry({
        itemId,
        gridId,
        siteId: sid,
        channel,
        local: receiver,
        startClient: start,
        onEnd: () => {
          carry = null;
        },
      });
      carry = c ? Object.assign(c, { itemId, gridId }) : null;
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== "Alt" || !carry) return;
      const { itemId, gridId, lastClient } = carry;
      // The ghost's box BEFORE cancel disposes it — the restart rect when
      // the real item element is gone (e.g. scrolled out of a virtualized
      // canvas); when the element is mounted the adapter measures it itself.
      const ghostRect = carry.lastRect();
      carry.cancel();
      carry = null;
      // Mirror switch: hand the still-held gesture back to the move drag via
      // the point-based starter (grid 0.9.4 — no fabricated React event).
      const el = document.querySelector(
        `[data-grid-item="${typeof CSS !== "undefined" && CSS.escape ? CSS.escape(itemId) : itemId}"]`,
      );
      adapterRef.current.startCrossGridDragAt(
        { clientX: lastClient.x, clientY: lastClient.y },
        {
          itemId,
          fromGridId: gridId,
          kind: "app",
          ...(el || !ghostRect ? {} : { rect: ghostRect }),
        },
      );
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      carry?.cancel();
      receiver.dispose();
      channel.close();
    };
  }, [readOnly]);
}
